import { useRef } from 'react';
import { useDialogA11y } from '../../hooks/useDialogA11y';

// Space/channel picker shown when the Android wrapper forwards a share intent.
// Structural subsets of ChatLayout's types (ground rule 5: no back-imports).
type SharePayload = { text?: string; files: unknown[] };
type Space = { id: string; name?: string | null };
type Channel = { id: string; name?: string | null };

type AndroidShareModalProps = {
  payload: SharePayload;
  spaceId: string | null;
  channelId: string | null;
  currentVoidId: string;
  voids: Space[];
  accessibleChannels: Channel[];
  channelCount: number;
  getDmDisplayName: (space?: { id?: string; name?: string | null } | null, fallbackId?: string) => string;
  onSelectSpace: (spaceId: string | null) => void;
  onSetChannelId: (channelId: string | null) => void;
  onCancel: () => void;
  onShare: () => void;
};

function AndroidShareModal({
  payload,
  spaceId,
  channelId,
  currentVoidId,
  voids,
  accessibleChannels,
  channelCount,
  getDmDisplayName,
  onSelectSpace,
  onSetChannelId,
  onCancel,
  onShare,
}: AndroidShareModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(true, dialogRef, onCancel);
  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onCancel}
      />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="android-share-title" tabIndex={-1} className="relative w-full max-w-md rounded-2xl border border-neutral-800 bg-neutral-950 p-5 shadow-2xl space-y-4 outline-none">
        <div className="space-y-1">
          <div id="android-share-title" className="text-lg font-medium text-fg">Share to Echo</div>
          <p className="text-sm text-fg-2">
            Choose a space and channel to share your content.
          </p>
        </div>
        <div className="space-y-3">
          <label className="space-y-1 text-sm text-fg">
            <span className="text-xs text-fg-3">Space</span>
            <select
              className="w-full rounded-xl border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-fg focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              value={spaceId || ""}
              onChange={(e) => onSelectSpace(e.target.value || null)}
            >
              <option value="" disabled>
                Select a space
              </option>
              {voids.map((v) => (
                <option key={v.id} value={v.id}>
                  {String(v.id).startsWith("dm_") ? getDmDisplayName(v, v.id) : (v.name || "Untitled space")}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-sm text-fg">
            <span className="text-xs text-fg-3">Channel</span>
            <select
              className="w-full rounded-xl border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-fg focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              value={channelId || ""}
              onChange={(e) => onSetChannelId(e.target.value || null)}
              disabled={!spaceId || spaceId !== currentVoidId || channelCount === 0}
            >
              <option value="" disabled>
                {spaceId && spaceId !== currentVoidId ? "Loading channels..." : "Select a channel"}
              </option>
              {spaceId === currentVoidId &&
                accessibleChannels.map((c) => (
                  <option key={c.id} value={c.id}>
                    #{c.name || c.id}
                  </option>
                ))}
            </select>
          </label>
          <div className="rounded-xl border border-neutral-800 bg-neutral-900/70 px-3 py-2 text-xs text-fg-2">
            <div>{payload.text ? "Text included" : "No text included"}</div>
            <div>{payload.files.length} file(s)</div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            className="px-4 py-2 rounded-full border border-neutral-700 text-fg hover:bg-neutral-900/60"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-full bg-emerald-600 text-emerald-50 hover:bg-emerald-500 disabled:opacity-60"
            disabled={!spaceId || !channelId}
            onClick={onShare}
          >
            Share
          </button>
        </div>
      </div>
    </div>
  );
}

export default AndroidShareModal;
